import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { FirstVisitGate } from './components/FirstVisitGate';

// Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import PeptideCalculatorPage from './pages/PeptideCalculatorPage';
import CartPage from './pages/CartPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import AuthPage from './pages/AuthPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import DashboardPage from './pages/DashboardPage';
import COAsPage from './pages/COAsPage';
import ResearchInsightsPage from './pages/ResearchInsightsPage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import AdminPage from './pages/AdminPage';
import WholesalePage from './pages/WholesalePage';
import StaffPage from './pages/StaffPage';
import ConsentPage from './pages/ConsentPage';
import UnsubscribePage from './pages/UnsubscribePage';
import ShopPage from './pages/ShopPage';
import ResourcesPage from './pages/ResourcesPage';
import CustomOrderPage from './pages/CustomOrderPage';
import CatalogPage from './pages/CatalogPage';
import PreferencesPage from './pages/PreferencesPage';
import ParticlesBackground from './components/ParticlesBackground';
import MaintenancePage from './components/MaintenancePage';
import PhonePromptModal from './components/PhonePromptModal';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';

// Layout
import Layout from './components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Cart Context
export const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

// Wishlist Context
export const WishlistContext = createContext(null);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('authToken'));

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const authToken = localStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    return headers;
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const authToken = localStorage.getItem('authToken');
    
    // Skip auth check if no token exists (avoids unnecessary 401 errors)
    if (!authToken) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(authToken);
      } else {
        // Clear invalid token
        localStorage.removeItem('authToken');
        setToken(null);
      }
    } catch (error) {
      // Silently handle network errors - user will just be logged out
      localStorage.removeItem('authToken');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ email, password })
      });
      
      // Store ok status before reading body (Safari compatibility)
      const isOk = response.ok;
      const status = response.status;
      
      let responseData;
      try {
        const text = await response.text();
        responseData = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('Login response parse error:', parseError, 'Status:', status);
        throw new Error(status === 0 ? 'Network error - please check your connection' : `Server error (${status}). Please try again.`);
      }
      
      if (!isOk) {
        const errorMessage = responseData?.detail || `Login failed (${status})`;
        throw new Error(String(errorMessage));
      }
      
      // Store token in localStorage and state
      if (responseData.token) {
        localStorage.setItem('authToken', responseData.token);
        setToken(responseData.token);
      }
      setUser(responseData.user);
      return responseData;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Login failed');
    }
  };

  const register = async (name, email, password, phone, prefs = {}) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          name, email, password, phone,
          newsletter_opt_in: prefs.newsletterOptIn !== false,
          inventory_opt_in: prefs.inventoryOptIn !== false,
        })
      });
      
      // Store ok status before reading body (Safari compatibility)
      const isOk = response.ok;
      const status = response.status;
      
      let responseData;
      try {
        const text = await response.text();
        responseData = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('Register response parse error:', parseError, 'Status:', status);
        throw new Error(status === 0 ? 'Network error - please check your connection' : `Server error (${status}). Please try again.`);
      }
      
      if (!isOk) {
        const errorMessage = responseData?.detail || `Registration failed (${status})`;
        throw new Error(String(errorMessage));
      }
      
      // Store token in localStorage and state
      if (responseData.token) {
        localStorage.setItem('authToken', responseData.token);
        setToken(responseData.token);
      }
      setUser(responseData.user);
      return responseData;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Registration failed');
    }
  };

  const logout = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, loginWithGoogle, checkAuth, getAuthHeaders, token }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Provider
const CartProvider = ({ children }) => {
  const { settings: siteSettings } = useSiteSettings();
  const cartCountdownEnabled = !!siteSettings?.cart_countdown_enabled;
  const [items, setItems] = useState(() => {
    // Initialize from localStorage immediately to prevent save effect from clearing
    const savedCart = localStorage.getItem('genora_cart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        // Filter out invalid items (missing name, price, or NaN price)
        return parsed.filter(item => 
          item && 
          item.product_id && 
          item.name && 
          typeof item.price === 'number' && 
          !isNaN(item.price)
        );
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  const [loading, setLoading] = useState(false);
  const [cartLoaded, setCartLoaded] = useState(false);
  const { user } = useAuth();

  // Load cart from localStorage on mount, then sync with server if logged in
  useEffect(() => {
    const loadCart = async () => {
      // Helper function to validate cart items
      const filterValidItems = (items) => {
        return items.filter(item => 
          item && 
          item.product_id && 
          item.name && 
          typeof item.price === 'number' && 
          !isNaN(item.price)
        );
      };
      
      // First load from localStorage for immediate display
      const savedCart = localStorage.getItem('genora_cart');
      if (savedCart) {
        try {
          const parsed = JSON.parse(savedCart);
          setItems(filterValidItems(parsed));
        } catch (e) {
          console.error('Error parsing saved cart:', e);
        }
      }
      
      // If logged in, fetch cart from server and merge
      if (user) {
        try {
          const authToken = localStorage.getItem('authToken');
          const response = await fetch(`${API_URL}/api/cart`, { 
            
            headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
          });
          if (response.ok) {
            const serverCart = await response.json();
            if (serverCart.items && serverCart.items.length > 0) {
              // Filter valid items from server
              const validServerItems = filterValidItems(serverCart.items);
              
              // Merge server cart with local cart (server takes precedence)
              const mergedItems = [...validServerItems];
              const serverProductIds = new Set(validServerItems.map(i => `${i.product_id}-${i.vial_size}`));
              
              // Add local items that aren't on server
              const localCart = savedCart ? JSON.parse(savedCart) : [];
              const validLocalItems = filterValidItems(localCart);
              validLocalItems.forEach(localItem => {
                const key = `${localItem.product_id}-${localItem.vial_size}`;
                if (!serverProductIds.has(key)) {
                  mergedItems.push(localItem);
                }
              });
              
              setItems(mergedItems);
              localStorage.setItem('genora_cart', JSON.stringify(mergedItems));
            }
          }
        } catch (error) {
          console.error('Error fetching server cart:', error);
        }
      }
      setCartLoaded(true);
    };
    
    loadCart();
  }, [user]);

  // Save cart to localStorage and server when it changes (only after initial load)
  useEffect(() => {
    if (!cartLoaded) return; // Don't save until cart has been loaded
    
    localStorage.setItem('genora_cart', JSON.stringify(items));
    
    // Sync to server if logged in
    if (user && items.length >= 0) {
      const syncCart = async () => {
        try {
          const authToken = localStorage.getItem('authToken');
          await fetch(`${API_URL}/api/cart`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
            },
            
            body: JSON.stringify({ items })
          });
        } catch (error) {
          console.error('Error syncing cart to server:', error);
        }
      };
      
      // Debounce the sync
      const timeoutId = setTimeout(syncCart, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [items, user, cartLoaded]);

  const addItem = (product, quantity = 1) => {
    // Validate product data before adding
    if (!product || !product.product_id || !product.name || typeof product.price !== 'number' || isNaN(product.price)) {
      console.error('Invalid product data:', product);
      return;
    }
    
    setItems(prev => {
      // Mixed carts (in-stock + preorder + shop accessory) are now allowed —
      // backend deducts inventory only for non-preorder lines and ships in-stock
      // items immediately, so the user no longer has to place separate orders.

      const existing = prev.find(item => 
        item.product_id === product.product_id && item.vial_size === product.vial_size
      );
      
      const currentQty = existing ? existing.quantity : 0;
      const newTotalQty = currentQty + quantity;
      const stockQty = product.stock_quantity;
      
      // Stock limit check (skip for pre-orders or if stock info not available)
      if (!product.is_preorder && typeof stockQty === 'number' && stockQty > 0 && newTotalQty > stockQty) {
        setTimeout(() => toast.error(
          `Only ${stockQty} unit${stockQty > 1 ? 's' : ''} of ${product.name}${product.vial_size ? ` (${product.vial_size})` : ''} in stock. You can pre-order additional units.`,
          { duration: 5000 }
        ), 0);
        // Cap at stock quantity
        if (currentQty >= stockQty) return prev;
        const cappedQty = stockQty;
        if (existing) {
          return prev.map(item =>
            item.product_id === product.product_id && item.vial_size === product.vial_size
              ? { ...item, quantity: cappedQty }
              : item
          );
        }
        return [...prev, { ...product, quantity: cappedQty - currentQty }];
      }
      
      const name = product.name || 'Item';
      const size = product.vial_size || '';
      const label = product.is_preorder ? 'pre-ordered' : 'added to cart';
      setTimeout(() => toast.success(`${name}${size ? ` (${size})` : ''} ${label}`), 0);
      
      if (existing) {
        return prev.map(item =>
          item.product_id === product.product_id && item.vial_size === product.vial_size
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity, added_at: Date.now() }];
    });
  };

  // Cart-item TTL: items auto-expire after 15 minutes so they don't artificially
  // block other customers from seeing fresh stock. (We don't actually reserve
  // stock in the DB on add — this is purely a UX nudge so the cart matches
  // reality.) Any quantity change resets the timer for that line.
  const CART_TTL_MS = 15 * 60 * 1000;
  const itemsRef = React.useRef(items);
  React.useEffect(() => { itemsRef.current = items; }, [items]);

  useEffect(() => {
    // Only sweep/expire cart lines when the admin has enabled the countdown.
    if (!cartCountdownEnabled) return;
    const tick = () => {
      const current = itemsRef.current;
      if (!current || current.length === 0) return;
      const now = Date.now();
      const survivors = current.filter(it => !it.added_at || now - it.added_at < CART_TTL_MS);
      const removedCount = current.length - survivors.length;
      if (removedCount === 0) {
        // Legacy back-fill (items added before this update have no added_at).
        if (current.some(it => !it.added_at)) {
          setItems(current.map(it => (it.added_at ? it : { ...it, added_at: now })));
        }
        return;
      }
      setItems(survivors);
      toast(
        removedCount === 1
          ? 'An item was removed from your cart after 15 minutes — add it again if it’s still in stock.'
          : `${removedCount} items were removed from your cart after 15 minutes — add them again if still in stock.`,
        { icon: '🛒', duration: 6000 }
      );
    };
    tick();
    const id = setInterval(tick, 30 * 1000);
    return () => clearInterval(id);
  }, [CART_TTL_MS, cartCountdownEnabled]);

  const removeItem = (productId, vialSize = null) => {
    setItems(prev => prev.filter(item => {
      if (vialSize) {
        return !(item.product_id === productId && item.vial_size === vialSize);
      }
      return item.product_id !== productId;
    }));
  };

  const updateQuantity = (productId, quantity, vialSize = null) => {
    if (quantity <= 0) {
      removeItem(productId, vialSize);
      return;
    }
    setItems(prev =>
      prev.map(item => {
        const isMatch = vialSize
          ? (item.product_id === productId && item.vial_size === vialSize)
          : (item.product_id === productId);
        if (!isMatch) return item;
        
        // Stock limit check
        if (!item.is_preorder && typeof item.stock_quantity === 'number' && item.stock_quantity > 0 && quantity > item.stock_quantity) {
          setTimeout(() => toast.error(
            `Only ${item.stock_quantity} in stock. You can pre-order additional units separately.`,
            { duration: 4000 }
          ), 0);
          return { ...item, quantity: item.stock_quantity, added_at: Date.now() };
        }
        // Reset the TTL on user interaction so an active customer doesn't get expired mid-checkout.
        return { ...item, quantity, added_at: Date.now() };
      })
    );
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem('genora_cart');
  };

  const total = items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, total, itemCount, loading, cartCountdownEnabled }}>
      {children}
    </CartContext.Provider>
  );
};

// Wishlist Provider
const WishlistProvider = ({ children }) => {
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchWishlist();
    } else {
      setWishlistIds(new Set());
    }
  }, [user]);

  const fetchWishlist = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/wishlist`, {
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
      if (response.ok) {
        const items = await response.json();
        setWishlistIds(new Set(items.map(i => i.product_id)));
      }
    } catch {
      // non-critical: ignore wishlist fetch failures
    }
  };

  const toggleWishlist = async (productId) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return false;

    const isInWishlist = wishlistIds.has(productId);
    
    // Optimistic update
    setWishlistIds(prev => {
      const next = new Set(prev);
      if (isInWishlist) next.delete(productId);
      else next.add(productId);
      return next;
    });

    try {
      const response = await fetch(`${API_URL}/api/wishlist/${productId}`, {
        method: isInWishlist ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        // Revert on failure
        setWishlistIds(prev => {
          const next = new Set(prev);
          if (isInWishlist) next.add(productId);
          else next.delete(productId);
          return next;
        });
        return false;
      }
      return true;
    } catch {
      return false;
    }
  };

  const isInWishlist = (productId) => wishlistIds.has(productId);

  return (
    <WishlistContext.Provider value={{ wishlistIds, toggleWishlist, isInWishlist, refreshWishlist: fetchWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 0);
  }, [pathname]);

  return null;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  const { settings: siteSettings } = useSiteSettings();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  // Maintenance gate. When an admin flips `maintenance_mode` ON, the
  // storefront is replaced by the "We'll be back soon" page. /admin and
  // /staff stay reachable so the team can turn it back off — and the
  // browser can still serve cached pages while we're fetching settings on
  // the first paint (loading=true is treated as "not in maintenance" to
  // avoid a flicker for healthy visits).
  if (
    siteSettings?.maintenance_mode &&
    !location.pathname.startsWith('/admin') &&
    !location.pathname.startsWith('/staff')
  ) {
    return (
      <MaintenancePage
        message={siteSettings.maintenance_message}
        contactEmail={siteSettings.contact_email}
        contactPhone={siteSettings.contact_phone}
      />
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Admin page without Layout (no header/footer) */}
      <Route path="admin" element={<AdminPage />} />
      {/* Staff page without Layout */}
      <Route path="staff" element={<StaffPage />} />
      
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/:slug" element={<ProductDetailPage />} />
        <Route path="calculator" element={<ProtectedRoute><PeptideCalculatorPage /></ProtectedRoute>} />
        <Route path="cart" element={<CartPage />} />
        <Route path="checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="auth" element={<AuthPage />} />
        <Route path="auth/callback" element={<AuthCallbackPage />} />
        <Route path="dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="shop" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
        <Route path="catalog" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
        <Route path="resources" element={<ProtectedRoute><ResourcesPage /></ProtectedRoute>} />
        <Route path="coas" element={<COAsPage />} />
        <Route path="research-insights" element={<ResearchInsightsPage />} />
        <Route path="research-insights/:slug" element={<ArticleDetailPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="wholesale" element={<WholesalePage />} />
        <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="consent/:token" element={<ConsentPage />} />
        <Route path="unsubscribe/:token" element={<UnsubscribePage />} />
        <Route path="preferences" element={<PreferencesPage />} />
        <Route path="custom-order/:token" element={<CustomOrderPage />} />
      </Route>
    </Routes>
    </>
  );
};

function App() {
  const [ageVerified, setAgeVerified] = useState(() => {
    // Check localStorage for age verification with 30-day expiry
    const stored = localStorage.getItem('ageVerified');
    if (stored) {
      try {
        const { verified, expiry } = JSON.parse(stored);
        if (verified && expiry && new Date().getTime() < expiry) {
          return true;
        }
        // Expired, remove it
        localStorage.removeItem('ageVerified');
      } catch (e) {
        localStorage.removeItem('ageVerified');
      }
    }
    return false;
  });

  const handleGateComplete = () => {
    // Persist verification with 30-day expiry, then dismiss the gate.
    const expiry = new Date().getTime() + (30 * 24 * 60 * 60 * 1000);
    localStorage.setItem('ageVerified', JSON.stringify({ verified: true, expiry }));
    setAgeVerified(true);
  };

  const handleGateExit = () => {
    // Visitor does not meet the requirements — redirect away.
    window.location.href = 'https://www.google.com';
  };

  return (
    <BrowserRouter>
      <SiteSettingsProvider>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <ParticlesBackground />
              {!ageVerified && <FirstVisitGate onComplete={handleGateComplete} onExit={handleGateExit} />}
              <AppRouter />
              <PhonePromptModal />
              <Toaster position="top-right" richColors />
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </SiteSettingsProvider>
    </BrowserRouter>
  );
}

export default App;
